import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Check, Play, X, Zap, Shield, Camera, Droplets, Gauge, Leaf } from 'lucide-react';
import { droneImage, videoUrl } from '../data/mock';

const KeelcrabPage = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const features = [
    {
      icon: Zap,
      title: "Performance maximale",
      description: "Nettoyage rapide et efficace grâce à un système de brossage robotisé de haute technologie."
    },
    {
      icon: Camera,
      title: "Caméra HD intégrée",
      description: "Inspection en temps réel et enregistrement vidéo HD fourni après chaque intervention."
    },
    {
      icon: Shield,
      title: "Filtre 160 microns",
      description: "Retient efficacement tous les déchets, zéro rejet dans l'eau."
    },
    {
      icon: Droplets,
      title: "Sans produits chimiques",
      description: "Nettoyage 100% mécanique, respectueux de l'environnement lacustre."
    },
    {
      icon: Gauge,
      title: "Contrôle précis",
      description: "Pilotage professionnel permettant d'atteindre toutes les zones de la coque."
    },
    {
      icon: Leaf,
      title: "Écologique",
      description: "Conforme aux normes environnementales suisses les plus strictes."
    }
  ];

  const specifications = [
    { label: "Dimensions", value: "60 x 40 x 25 cm" },
    { label: "Poids", value: "15 kg" },
    { label: "Profondeur max", value: "30 mètres" },
    { label: "Autonomie", value: "4 heures" },
    { label: "Caméra", value: "HD 1080p" },
    { label: "Filtration", value: "160 microns" },
    { label: "Conception", value: "Made in Italy" },
    { label: "Certification", value: "CE" }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  Technologie de pointe
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
                  Robot <span className="text-blue-600">Keelcrab Pro</span>
                </h1>
                <p className="text-lg text-slate-600 mb-8">
                  Le premier drone sous-marin entièrement conçu et fabriqué en Italie pour effectuer des activités de maintenance et d'inspection robotisées sur votre coque.
                </p>
                
                <ul className="space-y-3 mb-8">
                  {['Nettoyage sans mise au sec', 'Inspection vidéo incluse', 'Zéro rejet dans l\'eau', 'Intervention rapide'].map((item, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setIsVideoOpen(true)}
                  className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105"
                >
                  <Play className="w-5 h-5" />
                  <span>Voir en action</span>
                </button>
              </div>

              <div className="relative">
                <div className="bg-white rounded-3xl shadow-2xl p-6">
                  <img
                    src={droneImage}
                    alt="Drone Keelcrab Pro"
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-blue-600/20 rounded-full blur-3xl" />
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                Caractéristiques
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Caractéristiques <span className="text-blue-600">exceptionnelles</span>
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Le Keelcrab Pro combine innovation technologique et respect de l'environnement pour un nettoyage optimal de votre coque.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-2 group border border-slate-100"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Specifications */}
        <section className="py-24 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  Fiche technique
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Spécifications <span className="text-blue-400">techniques</span>
                </h2>
                <p className="text-slate-400 mb-8">
                  Le Keelcrab Pro est conçu pour offrir des performances optimales dans les conditions les plus exigeantes des lacs suisses.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {specifications.map((spec, index) => (
                    <div key={index} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                      <p className="text-slate-400 text-sm mb-1">{spec.label}</p>
                      <p className="text-white font-semibold">{spec.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                  <iframe
                    src="https://www.youtube.com/embed/4DRgeDxJmjI"
                    title="Keelcrab Pro - Démonstration"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                Notre processus
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Comment ça <span className="text-blue-600">fonctionne ?</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Contact', desc: 'Demandez votre devis gratuit' },
                { step: '2', title: 'Rendez-vous', desc: 'Nous convenons d\'une date d\'intervention' },
                { step: '3', title: 'Nettoyage', desc: 'Le drone nettoie votre coque en quelques heures' },
                { step: '4', title: 'Rapport', desc: 'Recevez la vidéo d\'inspection de votre coque' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <div className="relative w-full max-w-5xl">
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-blue-400 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                src={`${videoUrl}?autoplay=1`}
                title="Drone Keelcrab en action"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeelcrabPage;
